import React, { useMemo } from 'react';
import { Checkbox } from '@attentive-platform/stem-ui';

import ExampleCards from './ExampleCards';
import ModifierInput from './ModifierInput';
import { ScopeItemStyles as S } from './styles';

const ElementChip = ({ label, on, onClick, disabled }) => {
  if (disabled) return <S.ChipDisabled>{label}</S.ChipDisabled>;
  const Cmp = on ? S.ChipOn : S.ChipOff;
  return <Cmp onClick={onClick}>{label}</Cmp>;
};

const ScopeItem = ({
  scope,
  checked,
  elementsOn,
  modifierAnswers,
  onToggleScope,
  onToggleElement,
  onModifierChange,
  readOnly
}) => {
  const elements = scope.elements || [];
  const modifiers = scope.modifiers || [];
  const { questionMods, exampleMods } = useMemo(() => {
    const questions = [];
    const examples = [];
    (scope.modifiers || []).forEach(m => {
      if (!m) return;
      if (m.modifier_type === 'example') examples.push(m);
      else questions.push(m);
    });
    return { questionMods: questions, exampleMods: examples };
  }, [scope.modifiers]);
  const isAdvanced = scope.complexity === 'advanced';
  const elementsPicked = elements.filter(e => elementsOn[e.id]).length;
  const allOff = checked && elements.length > 0 && elementsPicked === 0;
  const elementIds = elements.map(e => e.id);
  const modifierIds = modifiers.map(m => m.id).filter(Boolean);

  const Card = checked ? S.CardOn : S.CardOff;
  const HeadRow = readOnly ? S.HeadRowStatic : S.HeadRowClickable;

  return (
    <Card>
      <HeadRow onClick={readOnly ? undefined : () => onToggleScope(scope.scope_id, elementIds, modifierIds)}>
        <S.CheckboxWrap onClick={e => e.stopPropagation()}>
          <Checkbox
            size='small'
            checked={checked}
            disabled={readOnly}
            onChange={() => onToggleScope(scope.scope_id, elementIds, modifierIds)}
          />
        </S.CheckboxWrap>
        <S.HeadBody>
          <S.TitleRow>
            <S.Title>{scope.scope_name}</S.Title>
            {isAdvanced && <S.AdvancedPill>advanced — may affect turnaround &amp; price</S.AdvancedPill>}
          </S.TitleRow>
          {scope.scope_description && <S.Description>{scope.scope_description}</S.Description>}
        </S.HeadBody>
      </HeadRow>

      {checked && (
        <S.ExpandedBlock>
          {elements.length > 0 && (
            <S.ElementsSection>
              <S.ElementsHeader>
                <S.ElementsHeading>Which elements should Beam measure?</S.ElementsHeading>
                <S.CountLabel>
                  {elementsPicked} of {elements.length}
                </S.CountLabel>
              </S.ElementsHeader>
              <S.ChipRow>
                {elements.map(el => (
                  <ElementChip
                    key={el.id}
                    label={el.name}
                    on={!!elementsOn[el.id]}
                    disabled={readOnly}
                    onClick={() => onToggleElement(el.id)}
                  />
                ))}
              </S.ChipRow>
              {allOff && (
                <S.AllOffWarning>
                  Nothing selected under this item — pick at least one element or untick the item.
                </S.AllOffWarning>
              )}
            </S.ElementsSection>
          )}

          {questionMods.length > 0 && (
            <S.ModifierSection>
              <S.ModifierHeading>How should Beam quantify it?</S.ModifierHeading>
              {questionMods.map(m => (
                <ModifierInput
                  key={m.id}
                  modifier={m}
                  value={modifierAnswers[m.id]}
                  onChange={patch => onModifierChange(m.id, patch)}
                  readOnly={readOnly}
                />
              ))}
            </S.ModifierSection>
          )}

          {exampleMods.length > 0 && <ExampleCards modifiers={exampleMods} />}
        </S.ExpandedBlock>
      )}
    </Card>
  );
};

export default ScopeItem;
